import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Switch,
  Stack,
  Typography,
  IconButton,
  Divider,
} from "@mui/material";
import { ChangeEvent, useEffect, useState } from "react";
import { CreateUserPayload } from "../../services/api/user";
import { ApiUser } from "../../services/api/user";
import { RequestError, useApi } from "../../services";
import { ErrorOnDialog, SelectBarragem, SelectCompany } from "../modals";
import { CompanyData } from "../../pages";
import { generateRandomHash, objectToHash } from "../../core";
import { Add } from "@mui/icons-material";
import { PhonesEditor } from "../phonesEditor";
import { FuncionarioFuncao } from "./funcionarioFuncoes";
import { User } from "../../context";

interface FuncionarioRegisterProps {
  open: boolean;
  sendPassword?: boolean;
  initialFuncionario?: User;
  userEmpreendimento: string;
  blockEmpreendimento?: boolean;

  onClose: () => void;
  onAdd?: (id: string) => void;
  onUpdate?: (id: string) => void;
}

export const FuncionarioRegister: React.FC<FuncionarioRegisterProps> = ({
  open,
  onAdd,
  onClose,
  onUpdate,
  sendPassword,
  initialFuncionario,
  userEmpreendimento,
  blockEmpreendimento
}) => {
  const api = useApi();

  const [empreendimento, setEmpreendimento] = useState<CompanyData>();
  const [error, setError] = useState<RequestError>();
  const [funcaoPreKey, setFuncaoPreKey] = useState("");
  const [funcionario, setFuncionario] = useState<CreateUserPayload>({
    nome: initialFuncionario?.nome ?? '',
    email: initialFuncionario?.email ?? '',
    telefones: initialFuncionario?.telefones ?? '',
    admin: false,
    sendPassword: sendPassword ?? false,
    funcoes: initialFuncionario?.funcoes ?? [],
    empreedimento: userEmpreendimento,
  });

  useEffect(() => {
    api.company.company(userEmpreendimento).then((value) => {
      if ("error" in value) {
        setError(value);
        return;
      }

      setEmpreendimento(value);
    });
  }, [userEmpreendimento]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFuncionario({ ...funcionario, [name]: value });
  };

  const handleSubmit = async () => {
    let response: string | RequestError;
    if (!initialFuncionario) {
      response = await ApiUser.create(funcionario);
    } else {
      const resp = await ApiUser.update(initialFuncionario.id, funcionario);
      if ("error" in resp) {
        setError(resp);
        return;
      }
      response = resp.id;
    }

    if (typeof response !== "string" && "error" in response) {
      setError(response);
      return;
    }

    setFuncionario({
      nome: '',
      email: '',
      telefones: '',
      funcoes: [],
      admin: false,
      sendPassword: false,
      empreedimento: userEmpreendimento,
    });

    if (initialFuncionario) {
      onUpdate?.(response);
    } else {
      onAdd?.(response);
    }
    onClose();
  };

  return (<>
    {error && <ErrorOnDialog
      open={Boolean(error)}
      error={error.error}
      message={error.message}
      onClose={() => setError(undefined)}
    />}

    <Dialog open={open} fullWidth onClose={onClose}>

      <DialogTitle bgcolor="primary.main" id="alert-dialog-title" color="#fff">
        {`${initialFuncionario ? "Editar" : "Cadastrar"} Funcionário`}
      </DialogTitle>

      <DialogContent id="dialog-content">

        <Box sx={{ my: 2 }}>
          {/* Nome */}
          <Box mt={1}>
            <TextField
              required
              id="nome-funcionario"
              variant="outlined"
              label="Nome"
              name="nome"
              fullWidth
              value={funcionario.nome}
              onChange={handleInputChange}
            />
          </Box>

          {/* E-mail */}
          <Box my={1}>
            <TextField
              required
              id="email-funcionario"
              variant="outlined"
              label="E-mail"
              name="email"
              fullWidth
              value={funcionario.email}
              onChange={handleInputChange}
            />
          </Box>

          {/* Adm e Send Password */}
          <Box sx={{ display: "flex", flexDirection: "row" }}>
            <Box m={1} display="flex" flexDirection="column" alignItems="center" justifyContent="center">
              <Typography>O funcionário é administrador?</Typography>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Typography>Não</Typography>
                <Switch
                  color="info"
                  checked={funcionario.admin}
                  onChange={(e) =>
                    setFuncionario({ ...funcionario, admin: e.target.checked })
                  }
                />
                <Typography>Sim</Typography>
              </Stack>
            </Box>

            <div style={{ flex: 1 }} />

            <Box m={1} display="flex" flexDirection="column" alignItems="center" justifyContent="center">
              <Typography>Enviar Senha por E-mail?</Typography>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Typography>Não</Typography>
                <Switch
                  color="info"
                  checked={funcionario.sendPassword}
                  onChange={(e) =>
                    setFuncionario({ ...funcionario, sendPassword: e.target.checked })
                  }
                />
                <Typography>Sim</Typography>
              </Stack>
            </Box>
          </Box>

          <Divider />

          {/* Telefones */}
          <Box my={1}>
            <Box sx={{ display: "flex", flexDirection: "row" }}>
              <Typography>
                Telefones:
              </Typography>
              <div style={{ flex: 1 }} />
              <IconButton onClick={() => {
                setFuncionario({
                  ...funcionario,
                  telefones: (funcionario.telefones ?? "") + ";",
                });
              }}>
                <Add />
              </IconButton>

            </Box>
            <PhonesEditor
              key={objectToHash(funcionario.telefones)}
              phones={funcionario.telefones ?? ""}
              onEdit={(phones) => {
                setFuncionario({
                  ...funcionario,
                  telefones: phones,
                });
              }}
            />
          </Box>

          <Divider />
        </Box>

        {/* Empreendimento e funções */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "15px" }}>

          {/* Empreendimento */}
          <SelectCompany
            key={objectToHash(empreendimento)}
            variant="outlined"
            initialCompany={empreendimento}
            onSelect={(company) => {
              if (!blockEmpreendimento && company?.id !== empreendimento?.id) {
                setEmpreendimento(company);
                setFuncionario({
                  ...funcionario,
                  funcoes: [],
                });
              }
            }}
          />

          {/* Nova função */}
          <Box sx={{ display: "flex", flexDirection: "row" }}>
            <Typography>
              Funções:
            </Typography>
            <div style={{ flex: 1 }} />
            <Box width={"56px"}>
              <SelectBarragem
                key={objectToHash(empreendimento)}
                variant="outlined"
                companyFilter={empreendimento}
                onSelect={(barragem) => {
                  if (!barragem) return;
                  setFuncionario({
                    ...funcionario,
                    funcoes: [
                      ...funcionario.funcoes ?? [],
                      {
                        id: -1,
                        funcionario: '',
                        barragem: barragem.id,
                      }
                    ]
                  });
                }}
              >
                <Add />
              </SelectBarragem>
            </Box>
          </Box>

          {/* Funções já selecionadas */}
          {empreendimento && funcionario.funcoes.map((funcao, idx) => {
            return <>
              <FuncionarioFuncao
                key={`${funcaoPreKey}_${idx}`}
                {...funcao}
                onChange={(funcao) => setFuncionario({
                  ...funcionario,
                  funcoes: funcionario.funcoes.map((fun, i) => i === idx ? funcao : fun),
                })}
                onRemove={() => {
                  setFuncionario({
                    ...funcionario,
                    funcoes: funcionario.funcoes.filter((_, i) => i !== idx),
                  });
                  setFuncaoPreKey(generateRandomHash(8));
                }}
              />
              <Divider />
            </>;
          })}


        </Box>

      </DialogContent>

      <DialogActions>
        <Button variant="contained" onClick={onClose}>
          Cancelar
        </Button>
        <Button onClick={handleSubmit}>
          {initialFuncionario ? "Editar" : "Cadastrar"}
        </Button>
      </DialogActions>
    </Dialog>
  </>
  );
};